import {
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  type FederatedPointerEvent,
  type FederatedWheelEvent
} from 'pixi.js';
import { BaseScene, type SceneServices } from './BaseScene';
import { createEvidenceImageReference, EVIDENCE_DEFINITIONS } from '../app/EvidenceCatalog';
import type { GameConfig } from '../app/GameConfig';
import { listCases } from '../app/ScenarioCatalog';
import type { EvidenceDefinition } from '../app/types';
import type { ViewportLayout } from '../app/Viewport';
import {
  Button,
  HeaderBar,
  Modal,
  Panel,
  UI_COLORS,
  UI_FONT_FAMILY,
  createTextLayoutDebugEntry,
  fitTextToBounds,
  type TextLayoutDebugState
} from '../ui';

export interface EvidenceSceneOptions {
  onBack?: () => void;
  onOpenCaseList?: () => void;
  onOpenSettings?: () => void;
  onGoToTitle?: () => void;
}

interface EvidenceRow {
  container: Container;
  background: Graphics;
  evidence: EvidenceDefinition;
  acquired: boolean;
  nameText: Text;
  statusText: Text;
}

interface CaseFilterEntry {
  button: Button;
  caseId: string;
  unlocked: boolean;
}

const EVIDENCES = EVIDENCE_DEFINITIONS;
const LIST_WIDTH = 420;
const DETAIL_WIDTH = 680;
const PANEL_HEIGHT = 560;
const ROW_WIDTH = LIST_WIDTH - 60;
const ROW_HEIGHT = 82;
const ROW_TEXT_WIDTH = ROW_WIDTH - 36;
const ROW_NAME_HEIGHT = 34;
const LIST_VIEWPORT_HEIGHT = PANEL_HEIGHT - 132;
const LIST_SCROLLBAR_X = ROW_WIDTH + 12;
const LIST_SCROLLBAR_WIDTH = 8;
const LIST_SCROLL_STEP = ROW_HEIGHT + 14;
const LIST_DRAG_THRESHOLD = 4;
const LIST_WHEEL_SCROLL_MULTIPLIER = 0.5;
const LIST_WHEEL_MIN_DISTANCE = 24;
const LIST_WHEEL_MAX_DISTANCE = 80;
const DETAIL_TEXT_WIDTH = DETAIL_WIDTH - 72;
const DETAIL_NAME_HEIGHT = 44;
const DETAIL_STATUS_HEIGHT = 24;
const DETAIL_IMAGE_WIDTH = 230;
const DETAIL_IMAGE_HEIGHT = 150;
const DETAIL_DESCRIPTION_HEIGHT = 198;
const DETAIL_META_HEIGHT = 62;
const FILTER_BUTTON_WIDTH = 62;
const FILTER_BUTTON_HEIGHT = 34;
const FILTER_BUTTON_GAP = 6;
const FILTER_LAYER_HEIGHT = 50;
const FILTER_VIEWPORT_WIDTH = ROW_WIDTH;
const FILTER_SCROLLBAR_Y = 42;
const FILTER_SCROLLBAR_HEIGHT = 5;
const FILTER_DRAG_THRESHOLD = 4;

export class EvidenceScene extends BaseScene {
  private readonly background = new Graphics();
  private readonly titleText = new Text({
    text: '証拠品',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 34,
      fontWeight: '900'
    }
  });
  private readonly subtitleText = new Text({
    text: '事件ファイルに記録された手がかり',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      fontWeight: '700'
    }
  });
  private readonly headerBar = new HeaderBar({
    width: 520,
    disabledActions: ['evidence'],
    onAction: (action) => this.handleHeaderAction(action)
  });
  private readonly listPanel = new Panel({
    width: LIST_WIDTH,
    height: PANEL_HEIGHT,
    title: '入手状況'
  });
  private readonly detailPanel = new Panel({
    width: DETAIL_WIDTH,
    height: PANEL_HEIGHT,
    title: '証拠詳細'
  });
  private readonly listViewport = new Container();
  private readonly rowLayer = new Container();
  private readonly listMask = new Graphics();
  private readonly listScrollTrack = new Graphics();
  private readonly listScrollThumb = new Graphics();
  private readonly filterViewport = new Container();
  private readonly filterButtonLayer = new Container();
  private readonly filterMask = new Graphics();
  private readonly filterScrollTrack = new Graphics();
  private readonly filterScrollThumb = new Graphics();
  private readonly detailNameText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 28,
      fontWeight: '900',
      wordWrap: true,
      wordWrapWidth: DETAIL_WIDTH - 72
    }
  });
  private readonly detailStatusText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      fontWeight: '800'
    }
  });
  private readonly detailImageFrame = new Graphics();
  private readonly detailImagePlaceholderText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      fontWeight: '900',
      align: 'center',
      wordWrap: true,
      wordWrapWidth: DETAIL_IMAGE_WIDTH - 24
    }
  });
  private readonly detailDescriptionText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 22,
      lineHeight: 34,
      wordWrap: true,
      wordWrapWidth: DETAIL_WIDTH - 72
    }
  });
  private readonly detailMetaText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 16,
      lineHeight: 24,
      wordWrap: true,
      wordWrapWidth: DETAIL_WIDTH - 72
    }
  });
  private readonly modalLayer = new Container();
  private readonly rows: EvidenceRow[] = [];
  private readonly caseFilterEntries: CaseFilterEntry[] = [];
  private layout: ViewportLayout | null = null;
  private noticeModal: Modal | null = null;
  private currentCaseFilterId: string;
  private selectedEvidenceId: string | null = null;
  private detailImageSprite: Sprite | null = null;
  private detailImageLoadToken = 0;
  private filterScrollOffset = 0;
  private filterDragActive = false;
  private filterDragMoved = false;
  private filterDragStartX = 0;
  private filterDragStartOffset = 0;
  private listScrollOffset = 0;
  private listDragActive = false;
  private listDragMoved = false;
  private listDragStartY = 0;
  private listDragStartOffset = 0;

  public constructor(
    services: SceneServices,
    private readonly config: GameConfig,
    private readonly options: EvidenceSceneOptions = {}
  ) {
    super(services);
    this.currentCaseFilterId = services.getState().getProgress().currentCaseId;
  }

  protected override onStart(): void {
    this.container.addChild(this.background);
    this.container.addChild(this.titleText);
    this.container.addChild(this.subtitleText);
    this.container.addChild(this.headerBar);
    this.container.addChild(this.listPanel);
    this.container.addChild(this.detailPanel);
    this.container.addChild(this.modalLayer);
    this.listPanel.content.addChild(this.filterViewport);
    this.listPanel.content.addChild(this.filterMask);
    this.listPanel.content.addChild(this.filterScrollTrack);
    this.listPanel.content.addChild(this.filterScrollThumb);
    this.filterViewport.addChild(this.filterButtonLayer);
    this.filterViewport.mask = this.filterMask;
    this.filterViewport.eventMode = 'static';
    this.filterViewport.cursor = 'default';
    this.filterViewport.hitArea = new Rectangle(0, 0, FILTER_VIEWPORT_WIDTH, FILTER_LAYER_HEIGHT);
    this.filterViewport.on('wheel', this.handleFilterWheel);
    this.filterViewport.on('pointerdown', this.handleFilterPointerDown);
    this.filterViewport.on('pointermove', this.handleFilterPointerMove);
    this.filterViewport.on('pointerup', this.handleFilterPointerUp);
    this.filterViewport.on('pointerupoutside', this.handleFilterPointerUp);
    this.listPanel.content.addChild(this.listViewport);
    this.listPanel.content.addChild(this.listMask);
    this.listPanel.content.addChild(this.listScrollTrack);
    this.listPanel.content.addChild(this.listScrollThumb);
    this.listViewport.addChild(this.rowLayer);
    this.listViewport.mask = this.listMask;
    this.listViewport.eventMode = 'static';
    this.listViewport.cursor = 'default';
    this.listViewport.hitArea = new Rectangle(0, 0, ROW_WIDTH, LIST_VIEWPORT_HEIGHT);
    this.listViewport.on('wheel', this.handleListWheel);
    this.listViewport.on('pointerdown', this.handleListPointerDown);
    this.listViewport.on('pointermove', this.handleListPointerMove);
    this.listViewport.on('pointerup', this.handleListPointerUp);
    this.listViewport.on('pointerupoutside', this.handleListPointerUp);
    this.detailPanel.content.addChild(this.detailNameText);
    this.detailPanel.content.addChild(this.detailStatusText);
    this.detailPanel.content.addChild(this.detailImageFrame);
    this.detailPanel.content.addChild(this.detailImagePlaceholderText);
    this.detailPanel.content.addChild(this.detailDescriptionText);
    this.detailPanel.content.addChild(this.detailMetaText);
    this.renderRows();
    this.selectEvidence(this.getDefaultSelectedEvidenceId());
    this.resize(this.services.getLayout());
  }

  protected override onDestroy(): void {
    this.destroyNoticeModal();
    this.destroyDetailImageSprite();
  }

  public override resize(layout: ViewportLayout): void {
    this.layout = layout;
    this.drawBackground(layout.baseWidth, layout.baseHeight);
    this.titleText.position.set(layout.safeArea.x, layout.safeArea.y + 16);
    this.subtitleText.position.set(layout.safeArea.x + 4, layout.safeArea.y + 60);
    this.headerBar.position.set(layout.safeArea.x + layout.safeArea.width - 520, layout.safeArea.y);
    this.listPanel.position.set(layout.safeArea.x, 120);
    this.detailPanel.position.set(layout.safeArea.x + LIST_WIDTH + 34, 120);
    this.layoutCaseFilters();
    this.layoutRows();
    this.renderSelectedDetail();
    this.noticeModal?.setViewportSize(layout.baseWidth, layout.baseHeight);
  }

  public override getTextLayoutDebugState(): TextLayoutDebugState {
    return {
      scene: 'evidence',
      entries: [
        ...this.rows.flatMap((row) => [
          createTextLayoutDebugEntry(
            `evidence.row.${row.evidence.id}.name`,
            row.nameText,
            ROW_TEXT_WIDTH,
            ROW_NAME_HEIGHT
          ),
          createTextLayoutDebugEntry(
            `evidence.row.${row.evidence.id}.status`,
            row.statusText,
            ROW_TEXT_WIDTH,
            22
          )
        ]),
        createTextLayoutDebugEntry(
          'evidence.detail.name',
          this.detailNameText,
          DETAIL_TEXT_WIDTH,
          DETAIL_NAME_HEIGHT
        ),
        createTextLayoutDebugEntry(
          'evidence.detail.status',
          this.detailStatusText,
          DETAIL_TEXT_WIDTH,
          DETAIL_STATUS_HEIGHT
        ),
        createTextLayoutDebugEntry(
          'evidence.detail.description',
          this.detailDescriptionText,
          DETAIL_TEXT_WIDTH,
          DETAIL_DESCRIPTION_HEIGHT
        ),
        createTextLayoutDebugEntry(
          'evidence.detail.meta',
          this.detailMetaText,
          DETAIL_TEXT_WIDTH,
          DETAIL_META_HEIGHT
        )
      ]
    };
  }

  private renderRows(): void {
    destroyRows(this.rows);
    this.rowLayer.removeChildren();

    this.renderCaseFilters();
    for (const evidence of this.getVisibleEvidences()) {
      const acquired = this.services.getState().hasEvidence(evidence.id);
      const row = this.createEvidenceRow(evidence, acquired);
      this.rows.push(row);
      this.rowLayer.addChild(row.container);
    }
  }

  private createEvidenceRow(evidence: EvidenceDefinition, acquired: boolean): EvidenceRow {
    const container = new Container();
    const background = new Graphics();
    const nameText = new Text({
      text: getEvidenceDisplayName(evidence, acquired),
      style: {
        fill: acquired ? UI_COLORS.ink : UI_COLORS.mutedInk,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 20,
        fontWeight: '800',
        wordWrap: true,
        wordWrapWidth: LIST_WIDTH - 84
      }
    });
    const statusText = new Text({
      text: acquired ? '入手済み' : '未入手',
      style: {
        fill: acquired ? UI_COLORS.gold : UI_COLORS.disabled,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 15,
        fontWeight: '800'
      }
    });

    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', (event: FederatedPointerEvent) => {
      event.stopPropagation();
      if (this.listDragMoved) {
        return;
      }
      this.selectEvidence(evidence.id);
    });
    container.addChild(background);
    container.addChild(nameText);
    container.addChild(statusText);

    return {
      container,
      background,
      evidence,
      acquired,
      nameText,
      statusText
    };
  }

  private layoutRows(): void {
    this.listViewport.position.set(0, FILTER_LAYER_HEIGHT);
    for (const [index, row] of this.rows.entries()) {
      const y = index * LIST_SCROLL_STEP;
      row.container.position.set(0, y);
      row.background.clear();
      row.background
        .roundRect(0, 0, ROW_WIDTH, ROW_HEIGHT, 6)
        .fill({
          color:
            row.evidence.id === this.selectedEvidenceId ? UI_COLORS.redDark : UI_COLORS.paperLight,
          alpha: row.evidence.id === this.selectedEvidenceId ? 0.92 : 0.76
        })
        .stroke({
          color: row.acquired ? UI_COLORS.gold : UI_COLORS.disabled,
          width: 2,
          alpha: row.evidence.id === this.selectedEvidenceId ? 0.92 : 0.46
        });
      row.nameText.position.set(18, 14);
      row.statusText.position.set(18, 50);
      fitTextToBounds(row.nameText, {
        maxWidth: ROW_TEXT_WIDTH,
        maxHeight: ROW_NAME_HEIGHT,
        baseFontSize: 20,
        minFontSize: 14,
        baseLineHeight: 24,
        minLineHeight: 18,
        wordWrap: true,
        breakWords: true
      });
      fitTextToBounds(row.statusText, {
        maxWidth: ROW_TEXT_WIDTH,
        maxHeight: 22,
        baseFontSize: 15,
        minFontSize: 12,
        baseLineHeight: 18,
        minLineHeight: 14,
        wordWrap: false,
        breakWords: false
      });
    }

    this.drawListViewport();
    this.setListScrollOffset(this.listScrollOffset);
  }

  private selectEvidence(evidenceId: string | null): void {
    this.selectedEvidenceId = evidenceId;
    this.layoutRows();
    this.renderSelectedDetail();
  }

  private renderSelectedDetail(): void {
    const visibleEvidences = this.getVisibleEvidences();
    const evidence =
      visibleEvidences.find((item) => item.id === this.selectedEvidenceId) ?? visibleEvidences[0];
    const acquired = evidence ? this.services.getState().hasEvidence(evidence.id) : false;

    if (!evidence) {
      this.detailNameText.text = '証拠なし';
      this.detailStatusText.text = '';
      this.renderDetailImage(null, false);
      this.detailDescriptionText.text = 'まだ確認できる証拠品はありません。';
      this.detailMetaText.text = '';
      return;
    }

    this.detailNameText.text = getEvidenceDisplayName(evidence, acquired);
    this.detailStatusText.text = acquired ? '入手済み' : '未入手';
    this.detailDescriptionText.text = acquired
      ? `${evidence.description}\n\n${evidence.detail ?? ''}`.trim()
      : 'まだ入手していない証拠品です。詳細は事件進行後に記録されます。';
    this.detailMetaText.text = acquired
      ? `関連事件: ${getCaseLabel(evidence.caseId)}\n記録状態: 事件ファイルに登録済み`
      : '未入手の証拠名と詳細は表示しません。';

    this.detailNameText.position.set(8, 0);
    this.detailStatusText.position.set(8, 46);
    this.detailImageFrame.position.set(DETAIL_TEXT_WIDTH - DETAIL_IMAGE_WIDTH, 0);
    this.detailImagePlaceholderText.position.set(DETAIL_TEXT_WIDTH - DETAIL_IMAGE_WIDTH + 12, 62);
    this.detailDescriptionText.position.set(8, 178);
    this.detailMetaText.position.set(8, 392);
    this.renderDetailImage(evidence, acquired);
    this.fitDetailText();
  }

  private renderDetailImage(evidence: EvidenceDefinition | null, acquired: boolean): void {
    const token = ++this.detailImageLoadToken;
    this.destroyDetailImageSprite();
    this.drawDetailImageFrame(acquired);
    this.detailImagePlaceholderText.text = acquired ? '画像読込中' : '未入手';
    this.detailImagePlaceholderText.visible = true;

    if (!evidence || !acquired) {
      return;
    }

    void this.services
      .getAssets()
      .loadImage(createEvidenceImageReference(evidence))
      .then((image) => {
        if (token !== this.detailImageLoadToken) {
          return;
        }

        this.detailImagePlaceholderText.visible = image.placeholder;
        this.detailImagePlaceholderText.text = image.placeholder ? '画像未配置' : '';
        const sprite = new Sprite(image.texture);
        fitSpriteToBox(sprite, DETAIL_IMAGE_WIDTH - 18, DETAIL_IMAGE_HEIGHT - 18);
        sprite.position.set(
          this.detailImageFrame.x + 9 + (DETAIL_IMAGE_WIDTH - 18 - sprite.width) / 2,
          this.detailImageFrame.y + 9 + (DETAIL_IMAGE_HEIGHT - 18 - sprite.height) / 2
        );
        this.detailImageSprite = sprite;
        this.detailPanel.content.addChildAt(
          sprite,
          this.detailPanel.content.getChildIndex(this.detailImagePlaceholderText)
        );
      });
  }

  private drawDetailImageFrame(acquired: boolean): void {
    this.detailImageFrame.clear();
    this.detailImageFrame
      .roundRect(0, 0, DETAIL_IMAGE_WIDTH, DETAIL_IMAGE_HEIGHT, 7)
      .fill({
        color: acquired ? UI_COLORS.black : UI_COLORS.paperLight,
        alpha: acquired ? 0.62 : 0.2
      })
      .stroke({ color: acquired ? UI_COLORS.gold : UI_COLORS.disabled, width: 2, alpha: 0.78 });
    this.detailImageFrame
      .rect(12, DETAIL_IMAGE_HEIGHT - 16, DETAIL_IMAGE_WIDTH - 24, 2)
      .fill({ color: UI_COLORS.gold, alpha: acquired ? 0.45 : 0.18 });
  }

  private fitDetailText(): void {
    fitTextToBounds(this.detailNameText, {
      maxWidth: DETAIL_TEXT_WIDTH,
      maxHeight: DETAIL_NAME_HEIGHT,
      baseFontSize: 28,
      minFontSize: 18,
      baseLineHeight: 34,
      minLineHeight: 24,
      wordWrap: true,
      breakWords: true
    });
    fitTextToBounds(this.detailStatusText, {
      maxWidth: DETAIL_TEXT_WIDTH,
      maxHeight: DETAIL_STATUS_HEIGHT,
      baseFontSize: 18,
      minFontSize: 13,
      baseLineHeight: 22,
      minLineHeight: 16,
      wordWrap: false,
      breakWords: false
    });
    fitTextToBounds(this.detailDescriptionText, {
      maxWidth: DETAIL_TEXT_WIDTH,
      maxHeight: DETAIL_DESCRIPTION_HEIGHT,
      baseFontSize: 22,
      minFontSize: 16,
      baseLineHeight: 34,
      minLineHeight: 23,
      wordWrap: true,
      breakWords: true
    });
    fitTextToBounds(this.detailMetaText, {
      maxWidth: DETAIL_TEXT_WIDTH,
      maxHeight: DETAIL_META_HEIGHT,
      baseFontSize: 16,
      minFontSize: 12,
      baseLineHeight: 24,
      minLineHeight: 16,
      wordWrap: true,
      breakWords: true
    });
  }

  private getDefaultSelectedEvidenceId(): string | null {
    const visibleEvidences = this.getVisibleEvidences();
    return (
      visibleEvidences.find((evidence) => this.services.getState().hasEvidence(evidence.id))?.id ??
      visibleEvidences[0]?.id ??
      null
    );
  }

  private getVisibleEvidences(): EvidenceDefinition[] {
    return EVIDENCES.filter((evidence) => evidence.caseId === this.currentCaseFilterId);
  }

  private renderCaseFilters(): void {
    for (const entry of this.caseFilterEntries.splice(0)) {
      entry.button.destroy({ children: true });
    }
    this.filterButtonLayer.removeChildren();

    for (const caseDefinition of listCases()) {
      const caseId = caseDefinition.id;
      const unlocked = this.services.getState().isCaseUnlocked(caseId);
      const button = new Button({
        label: `C${caseDefinition.displayOrder}`,
        width: FILTER_BUTTON_WIDTH,
        height: FILTER_BUTTON_HEIGHT,
        variant: caseId === this.currentCaseFilterId ? 'primary' : 'secondary',
        disabled: !unlocked,
        onPress: () => {
          if (this.filterDragMoved) {
            return;
          }

          this.selectCaseFilter(caseId);
        }
      });
      this.caseFilterEntries.push({ button, caseId, unlocked });
      this.filterButtonLayer.addChild(button);
    }

    this.layoutCaseFilters();
    this.ensureSelectedCaseFilterVisible();
  }

  private layoutCaseFilters(): void {
    for (const [index, entry] of this.caseFilterEntries.entries()) {
      entry.button.position.set(index * (FILTER_BUTTON_WIDTH + FILTER_BUTTON_GAP), 2);
    }
    this.drawFilterViewport();
    this.setFilterScrollOffset(this.filterScrollOffset);
  }

  private selectCaseFilter(caseId: string): void {
    if (this.currentCaseFilterId === caseId) {
      return;
    }

    this.currentCaseFilterId = caseId;
    this.listScrollOffset = 0;
    this.renderRows();
    this.selectEvidence(this.getDefaultSelectedEvidenceId());
  }

  private handleHeaderAction(
    action: 'save' | 'evidence' | 'caseList' | 'settings' | 'title'
  ): void {
    switch (action) {
      case 'save':
      case 'evidence':
        this.headerBar.releaseActionLock();
        break;
      case 'caseList':
        if (this.options.onOpenCaseList) {
          this.navigateAfterCurrentPointer(() => this.options.onOpenCaseList?.());
        } else {
          this.showNotice(
            '事件リスト',
            'いまは事件リストを開けません。タイトルから再度お試しください。'
          );
        }
        break;
      case 'settings':
        if (this.options.onOpenSettings) {
          this.navigateAfterCurrentPointer(() => this.options.onOpenSettings?.());
        } else {
          this.showNotice('設定', 'いまは設定画面を開けません。タイトルから再度お試しください。');
        }
        break;
	      case 'title':
	        this.navigateAfterCurrentPointer(() => this.options.onBack?.());
	        break;
	    }
	  }

  private navigateAfterCurrentPointer(navigate: () => void): void {
    this.services.getInput().lock();
    try {
      navigate();
    } finally {
      window.setTimeout(() => this.services.getInput().unlock(), 0);
    }
  }

  private showNotice(title: string, message: string): void {
    this.destroyNoticeModal();
    this.services.getInput().lock();
    const layout = this.layout;
    const modal = new Modal({
      title,
      message,
      viewportWidth: layout?.baseWidth ?? this.config.baseResolution.width,
      viewportHeight: layout?.baseHeight ?? this.config.baseResolution.height,
      actions: [
        {
          label: '閉じる',
          variant: 'primary',
          onPress: () => this.destroyNoticeModal()
        }
      ],
      onDismiss: () => this.destroyNoticeModal()
    });

    this.noticeModal = modal;
    this.modalLayer.addChild(modal);
  }

  private destroyNoticeModal(): void {
    if (!this.noticeModal) {
      return;
    }

    this.noticeModal.destroy({ children: true });
    this.noticeModal = null;
    this.services.getInput().unlock();
    this.headerBar.releaseActionLock();
  }

  private destroyDetailImageSprite(): void {
    if (!this.detailImageSprite) {
      return;
    }

    this.detailImageSprite.destroy();
    this.detailImageSprite = null;
  }

  private drawBackground(width: number, height: number): void {
    this.background.clear();
    this.background.rect(0, 0, width, height).fill(UI_COLORS.black);
    this.background.rect(0, 0, width, height).fill({ color: UI_COLORS.redDark, alpha: 0.18 });
    this.background.rect(0, 0, width, 8).fill({ color: UI_COLORS.red, alpha: 0.84 });
    this.background.rect(0, height - 8, width, 8).fill({ color: UI_COLORS.gold, alpha: 0.72 });
  }

  private readonly handleFilterWheel = (event: FederatedWheelEvent): void => {
    event.stopPropagation();
    const delta = Math.abs(event.deltaX) >= Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta === 0) {
      return;
    }

    const distance = Math.sign(delta) * Math.min(140, Math.max(36, Math.abs(delta)));
    this.setFilterScrollOffset(this.filterScrollOffset + distance);
  };

  private readonly handleListWheel = (event: FederatedWheelEvent): void => {
    event.stopPropagation();
    const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    if (delta === 0) {
      return;
    }

    const distance =
      Math.sign(delta) *
      Math.min(
        LIST_WHEEL_MAX_DISTANCE,
        Math.max(LIST_WHEEL_MIN_DISTANCE, Math.abs(delta) * LIST_WHEEL_SCROLL_MULTIPLIER)
      );
    this.setListScrollOffset(this.listScrollOffset + distance);
  };

  private readonly handleListPointerDown = (event: FederatedPointerEvent): void => {
    event.stopPropagation();
    this.listDragActive = this.getMaxListScrollOffset() > 0;
    this.listDragMoved = false;
    this.listDragStartY = event.global.y;
    this.listDragStartOffset = this.listScrollOffset;
    this.listViewport.cursor = getScrollableViewportCursor(
      this.getMaxListScrollOffset() > 0,
      this.listDragActive
    );
  };

  private readonly handleListPointerMove = (event: FederatedPointerEvent): void => {
    if (!this.listDragActive) {
      return;
    }

    event.stopPropagation();
    const deltaY = event.global.y - this.listDragStartY;
    if (Math.abs(deltaY) >= LIST_DRAG_THRESHOLD) {
      this.listDragMoved = true;
    }
    this.setListScrollOffset(this.listDragStartOffset - deltaY);
  };

  private readonly handleListPointerUp = (event: FederatedPointerEvent): void => {
    if (!this.listDragActive && !this.listDragMoved) {
      return;
    }

    event.stopPropagation();
    this.listDragActive = false;
    this.listViewport.cursor = getScrollableViewportCursor(this.getMaxListScrollOffset() > 0, false);
    window.setTimeout(() => {
      this.listDragMoved = false;
    }, 0);
  };

  private readonly handleFilterPointerDown = (event: FederatedPointerEvent): void => {
    event.stopPropagation();
    this.filterDragActive = this.getMaxFilterScrollOffset() > 0;
    this.filterDragMoved = false;
    this.filterDragStartX = event.global.x;
    this.filterDragStartOffset = this.filterScrollOffset;
    this.filterViewport.cursor = getScrollableViewportCursor(
      this.getMaxFilterScrollOffset() > 0,
      this.filterDragActive
    );
  };

  private readonly handleFilterPointerMove = (event: FederatedPointerEvent): void => {
    if (!this.filterDragActive) {
      return;
    }

    event.stopPropagation();
    const deltaX = event.global.x - this.filterDragStartX;
    if (Math.abs(deltaX) >= FILTER_DRAG_THRESHOLD) {
      this.filterDragMoved = true;
    }
    this.setFilterScrollOffset(this.filterDragStartOffset - deltaX);
  };

  private readonly handleFilterPointerUp = (event: FederatedPointerEvent): void => {
    if (!this.filterDragActive && !this.filterDragMoved) {
      return;
    }

    event.stopPropagation();
    this.filterDragActive = false;
    this.filterViewport.cursor = getScrollableViewportCursor(
      this.getMaxFilterScrollOffset() > 0,
      false
    );
    window.setTimeout(() => {
      this.filterDragMoved = false;
    }, 0);
  };

  private drawFilterViewport(): void {
    this.filterMask.clear();
    this.filterMask.rect(0, 0, FILTER_VIEWPORT_WIDTH, FILTER_LAYER_HEIGHT).fill(UI_COLORS.ink);

    const maxScrollOffset = this.getMaxFilterScrollOffset();
    const scrollbarVisible = maxScrollOffset > 0;
    this.filterScrollTrack.visible = scrollbarVisible;
    this.filterScrollThumb.visible = scrollbarVisible;
    this.filterViewport.cursor = getScrollableViewportCursor(scrollbarVisible, this.filterDragActive);

    this.filterScrollTrack.clear();
    this.filterScrollThumb.clear();
    if (!scrollbarVisible) {
      return;
    }

    this.filterScrollTrack
      .roundRect(0, FILTER_SCROLLBAR_Y, FILTER_VIEWPORT_WIDTH, FILTER_SCROLLBAR_HEIGHT, 3)
      .fill({ color: UI_COLORS.black, alpha: 0.42 })
      .stroke({ color: UI_COLORS.gold, width: 1, alpha: 0.32 });
    this.drawFilterScrollThumb();
  }

  private drawFilterScrollThumb(): void {
    const maxScrollOffset = this.getMaxFilterScrollOffset();
    this.filterScrollThumb.clear();
    if (maxScrollOffset <= 0) {
      return;
    }

    const contentWidth = this.getFilterContentWidth();
    const thumbWidth = Math.max(
      72,
      Math.min(
        FILTER_VIEWPORT_WIDTH,
        (FILTER_VIEWPORT_WIDTH / contentWidth) * FILTER_VIEWPORT_WIDTH
      )
    );
    const thumbTravel = Math.max(0, FILTER_VIEWPORT_WIDTH - thumbWidth);
    const thumbX = (this.filterScrollOffset / maxScrollOffset) * thumbTravel;
    this.filterScrollThumb
      .roundRect(thumbX, FILTER_SCROLLBAR_Y + 1, thumbWidth, FILTER_SCROLLBAR_HEIGHT - 2, 3)
      .fill({ color: UI_COLORS.gold, alpha: 0.82 });
  }

  private setFilterScrollOffset(offset: number): void {
    const nextOffset = Math.max(0, Math.min(offset, this.getMaxFilterScrollOffset()));
    this.filterScrollOffset = nextOffset;
    this.filterButtonLayer.position.set(-nextOffset, 0);
    this.updateFilterButtonInteraction();
    this.drawFilterScrollThumb();
  }

  private updateFilterButtonInteraction(): void {
    for (const entry of this.caseFilterEntries) {
      const x = entry.button.x - this.filterScrollOffset;
      const visible = x + FILTER_BUTTON_WIDTH > 0 && x < FILTER_VIEWPORT_WIDTH;
      entry.button.setDisabled(!entry.unlocked || !visible);
    }
  }

  private ensureSelectedCaseFilterVisible(): void {
    const selectedIndex = this.caseFilterEntries.findIndex(
      (entry) => entry.caseId === this.currentCaseFilterId
    );
    if (selectedIndex < 0) {
      this.setFilterScrollOffset(this.filterScrollOffset);
      return;
    }

    const selectedX = selectedIndex * (FILTER_BUTTON_WIDTH + FILTER_BUTTON_GAP);
    const selectedRight = selectedX + FILTER_BUTTON_WIDTH;
    if (selectedX < this.filterScrollOffset) {
      this.setFilterScrollOffset(selectedX);
      return;
    }

    if (selectedRight > this.filterScrollOffset + FILTER_VIEWPORT_WIDTH) {
      this.setFilterScrollOffset(selectedRight - FILTER_VIEWPORT_WIDTH);
    }
  }

  private getFilterContentWidth(): number {
    if (this.caseFilterEntries.length === 0) {
      return 0;
    }

    return (
      this.caseFilterEntries.length * FILTER_BUTTON_WIDTH +
      (this.caseFilterEntries.length - 1) * FILTER_BUTTON_GAP
    );
  }

  private getMaxFilterScrollOffset(): number {
    return Math.max(0, this.getFilterContentWidth() - FILTER_VIEWPORT_WIDTH);
  }

  private drawListViewport(): void {
    this.listMask.clear();
    this.listMask.rect(0, FILTER_LAYER_HEIGHT, ROW_WIDTH, LIST_VIEWPORT_HEIGHT).fill(UI_COLORS.ink);

    const maxScrollOffset = this.getMaxListScrollOffset();
    const scrollbarVisible = maxScrollOffset > 0;
    this.listScrollTrack.visible = scrollbarVisible;
    this.listScrollThumb.visible = scrollbarVisible;
    this.listViewport.cursor = getScrollableViewportCursor(scrollbarVisible, this.listDragActive);

    this.listScrollTrack.clear();
    this.listScrollThumb.clear();

    if (!scrollbarVisible) {
      return;
    }

    this.listScrollTrack
      .roundRect(
        LIST_SCROLLBAR_X,
        FILTER_LAYER_HEIGHT,
        LIST_SCROLLBAR_WIDTH,
        LIST_VIEWPORT_HEIGHT,
        4
      )
      .fill({ color: UI_COLORS.black, alpha: 0.42 })
      .stroke({ color: UI_COLORS.gold, width: 1, alpha: 0.36 });
    this.drawListScrollThumb();
  }

  private drawListScrollThumb(): void {
    const maxScrollOffset = this.getMaxListScrollOffset();
    this.listScrollThumb.clear();
    if (maxScrollOffset <= 0) {
      return;
    }

    const contentHeight = this.getListContentHeight();
    const thumbHeight = Math.max(
      64,
      Math.min(LIST_VIEWPORT_HEIGHT, (LIST_VIEWPORT_HEIGHT / contentHeight) * LIST_VIEWPORT_HEIGHT)
    );
    const thumbTravel = Math.max(0, LIST_VIEWPORT_HEIGHT - thumbHeight);
    const thumbY = FILTER_LAYER_HEIGHT + (this.listScrollOffset / maxScrollOffset) * thumbTravel;
    this.listScrollThumb
      .roundRect(LIST_SCROLLBAR_X + 1, thumbY, LIST_SCROLLBAR_WIDTH - 2, thumbHeight, 4)
      .fill({ color: UI_COLORS.gold, alpha: 0.82 });
  }

  private setListScrollOffset(offset: number): void {
    const nextOffset = Math.max(0, Math.min(offset, this.getMaxListScrollOffset()));
    this.listScrollOffset = nextOffset;
    this.rowLayer.position.set(0, -nextOffset);
    this.updateRowInteraction();
    this.drawListScrollThumb();
  }

  private updateRowInteraction(): void {
    for (const row of this.rows) {
      const y = row.container.y - this.listScrollOffset;
      const visible = y + ROW_HEIGHT > 0 && y < LIST_VIEWPORT_HEIGHT;
      row.container.eventMode = visible ? 'static' : 'none';
      row.container.cursor = visible ? 'pointer' : 'default';
    }
  }

  private getListContentHeight(): number {
    if (this.rows.length === 0) {
      return 0;
    }

    return this.rows.length * ROW_HEIGHT + (this.rows.length - 1) * (LIST_SCROLL_STEP - ROW_HEIGHT);
  }

  private getMaxListScrollOffset(): number {
    return Math.max(0, this.getListContentHeight() - LIST_VIEWPORT_HEIGHT);
  }
}

function getScrollableViewportCursor(scrollbarVisible: boolean, dragging: boolean): string {
  if (!scrollbarVisible) {
    return 'default';
  }

  return dragging ? 'grabbing' : 'grab';
}

function getEvidenceDisplayName(evidence: EvidenceDefinition, acquired: boolean): string {
  if (acquired) {
    return evidence.name;
  }

  return evidence.initiallyHidden ? '未確認の証拠' : '未入手の証拠';
}

function getCaseLabel(caseId: string): string {
  const match = /^case(\d+)$/i.exec(caseId);
  return match ? `Case ${match[1]}` : '事件ファイル';
}

function fitSpriteToBox(sprite: Sprite, maxWidth: number, maxHeight: number): void {
  const textureWidth = Math.max(1, sprite.texture.width);
  const textureHeight = Math.max(1, sprite.texture.height);
  const scale = Math.min(maxWidth / textureWidth, maxHeight / textureHeight);
  sprite.width = textureWidth * scale;
  sprite.height = textureHeight * scale;
}

function destroyRows(rows: EvidenceRow[]): void {
  for (const row of rows.splice(0)) {
    row.container.destroy({ children: true });
  }
}
